<template>
  <div class="card border mb-3">
    <div class="card-header">
      <h5 class="mb-0">{{ $t("forwards.deals.commitments.title") }}</h5>
      <p class="text-body-tertiary fs-10 mb-0">
        {{ $t("forwards.deals.commitments.description") }}
      </p>
    </div>

    <div class="card-body">
      <p v-if="rows.length === 0" class="text-body-tertiary mb-0">
        {{ $t("forwards.deals.commitments.empty") }}
      </p>

      <table v-else class="table table-sm mb-0">
        <thead>
          <tr>
            <th scope="col">
              {{ $t("forwards.deals.commitments.id_header") }}
            </th>
            <th scope="col">
              {{ $t("forwards.deals.commitments.pool_header") }}
            </th>
            <th scope="col">
              {{ $t("forwards.deals.commitments.pulled_on") }}
            </th>
            <th scope="col">
              {{ $t("forwards.deals.commitments.volume") }}
            </th>
            <th scope="col">
              {{ $t("forwards.deals.commitments.price_conditions") }}
            </th>
            <th
              scope="col"
              :title="$t('forwards.deals.commitments.released_tooltip')"
            >
              {{ $t("forwards.deals.commitments.released") }}
            </th>
            <th scope="col" class="text-end">
              <span class="visually-hidden">{{
                $t("forwards.common.actions")
              }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <CommitmentPullRowComponent
            v-for="row in rows"
            :key="row.commitment.id"
            :commitment="row.commitment"
            :poolId="row.poolId"
            :poolName="row.poolName"
            :entryWidth="row.entryWidth"
            @show-escrow="
              (commitmentId: number) => emit('show-escrow', commitmentId)
            "
          />
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import CommitmentPullRowComponent from "@/forwards2/deals/CommitmentPullRowComponent.vue";
import type {
  CommitmentGroup,
  CommitmentResponse,
} from "@/forwards2/forwardsTypes";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import { sortBy } from "lodash-es";
import { computed } from "vue";

interface LedgerRow {
  poolId: number;
  poolName: string;
  entryWidth: PeriodWidth | null;
  commitment: CommitmentResponse;
}

/**
 * What each pool owes this deal — a read-only ledger, one row per pull, the whole deal in one
 * table ordered by pool and then by the date the volume was taken.
 *
 * Secondary to the delivery plan on purpose: it answers "where does this volume come from and on
 * what price conditions", while the plan answers "what ships when" and owns every edit. Growing the
 * deal is therefore an open slice in Edit plan, backed from its own row — never a button here. One
 * modal serves every row's escrow breakdown, rather than one per commitment.
 */
const props = defineProps<{
  groups: CommitmentGroup[];
}>();

const emit = defineEmits<{
  "show-escrow": [commitmentId: number];
}>();

const rows = computed<LedgerRow[]>(() =>
  sortBy(
    props.groups.flatMap((group) =>
      group.commitments.map((commitment) => ({
        poolId: group.pool_id,
        poolName: group.pool_name,
        entryWidth: group.entry_width,
        commitment,
      }))
    ),
    [
      (row) => row.poolName.toLocaleLowerCase(),
      (row) => new Date(row.commitment.created_at).getTime(),
    ]
  )
);
</script>
